<script setup lang="ts">
/**
 * SectionErrorBoundary —— 动态 Section 渲染抛错的显式兜底（F07）。
 *
 * 包裹 HomePage grid 区的动态 `<component :is="resolveSection(type)">`，
 * 通过 `onErrorCaptured` 捕获子组件 setup/render 阶段抛出的异常 → 切到
 * 占位卡 + `console.error` 完整原因 + `return false` 阻断错误向上传播
 * （避免单个 Section 抛错把整页 HomePage 一起带走）。
 *
 * **fragment 根**：`v-if/v-else` 互斥渲染占位 `<section>` 或真实动态
 * 组件，boundary 自身无 wrapper DOM，不会遮蔽 HomePage CSS Grid 的
 * 直接子节点（ADR-0002 §修订记录 Q7：动态 Section 根节点必须直接
 * 作为 grid item，禁止额外 wrapper）。
 *
 * **Debug-First**：占位卡是用户可见显式提示，`console.error` 保留
 * 可观测信号；测试断言抛错后渲染占位 + 不影响其他 Section + console.error
 * 被调用。区别于 UnknownSection（type 未注册，F03），本组件兜底的是
 * "type 已注册但组件运行时抛错"。
 */
import { ref, onErrorCaptured, type Component } from 'vue';

import { copy } from '@/i18n/zh-CN';
import type { DataCardView, PublicSectionView } from '@/api/types';

const props = defineProps<{
  /** 已 resolve 的 Section 组件（resolveSection 结果，调用方 markRaw 避免 reactive 警告）。 */
  readonly component: Component;
  readonly section: PublicSectionView;
  /** 锚点 id，透传给占位 section / 动态组件，保持锚点可达。 */
  readonly id?: string;
  readonly dataCards?: readonly DataCardView[];
  /** 预览模式 Draft 角标透传（F08，HomePage 计算）。 */
  readonly showDraftBadge?: boolean;
  /** 预览态 token 透传（F10/F12，卡片型 Section DetailMoreLink 消费）。 */
  readonly previewToken?: string | null;
}>();

const failed = ref(false);

onErrorCaptured((error) => {
  failed.value = true;
  // eslint-disable-next-line no-console
  console.error(
    `[SectionErrorBoundary] section "${props.section.id}" (type=${props.section.type}) render threw`,
    error,
  );
  return false;
});
</script>

<template>
  <section
    v-if="failed"
    :id="props.id"
    :class="$style.placeholder"
    data-section-type="__error__"
    data-testid="section-error-placeholder"
    role="alert"
  >
    <h2 :class="$style.title">{{ copy.fallback.section.title }}</h2>
    <p :class="$style.hint">{{ copy.fallback.section.renderFailed }}</p>
  </section>
  <component
    v-else
    :is="props.component"
    :id="props.id"
    :section="props.section"
    :data-cards="props.dataCards"
    :show-draft-badge="props.showDraftBadge"
    :preview-token="props.previewToken"
  />
</template>

<style module>
.placeholder {
  background: var(--color-bg-secondary);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  color: var(--color-brand-primary);
}

.title {
  margin: 0 0 var(--space-1);
  color: var(--color-brand-primary);
  font-size: var(--font-size-md);
}

.hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}
</style>
